import { isNil, keyBy, cloneDeep } from 'lodash'
import {
  createCombinedInputFlows,
  EnergyFlow,
  EnergyType,
  isPrimaryEnergyFlow,
} from '../EnergyFlow'
import { HourlyEnergyFlow, PrimaryHourlyEnergyFlow } from '../HourlyEnergyFlow'
import {
  MultiTypeHourlyVariables,
  MultiTypeProvider,
} from './common/MultiTypeProvider'
import {
  DhwHeatingMode,
  HeatCircuit,
  HeatingOrCoolingSource,
  HybridHeatPumpParameters,
} from '../../generated/graphql'
import { OutdoorTemperatureMissingError } from '../calculator-error'
import { groupBy } from '../../util/array-utility'
import { preCalculateForHour } from './hybrid-heat-pump/pre-calculation'
import { calculateCirculationForHour } from './hybrid-heat-pump/circulation'
import { calculateHeatingForHour } from './hybrid-heat-pump/heating'
import { calculateCoolingForHour } from './hybrid-heat-pump/cooling'
import {
  calculateResultsForHour,
  ResultsCalculationResults,
} from './hybrid-heat-pump/results'
import { Provider } from './common/Provider'

type HybridHeatPumpHourlyVariables = Omit<
  MultiTypeHourlyVariables,
  'outdoorTemperature'
> & {
  outdoorTemperature: number
  wasteHeatSourceAPower?: number
  wasteHeatSourceBPower?: number
}

export enum HybridHeatPumpExtraOutputField {
  TotalHeating = 'totalHeating',
  RecycledHeatingBasic = 'recycledHeatingBasic',
  RecycledHeatingDhw = 'recycledHeatingDhw',
  HeatingBasic = 'heatingBasic',
  HeatingDhw = 'heatingDhw',
  TotalHeatingCop = 'totalHeatingCop',
  RecycledHeatingCopBasic = 'recycledHeatingCopBasic',
  RecycledHeatingCopDhw = 'recycledHeatingCopDhw',
  HeatingCopBasic = 'heatingCopBasic',
  HeatingCopDhw = 'heatingCopDhw',
  TotalCooling = 'totalCooling',
  RecycledCooling = 'recycledCooling',
  FreeCooling = 'freeCooling',
  ActiveCooling = 'activeCooling',
  TotalCoolingEer = 'totalCoolingEer',
  FreeCoolingEer = 'freeCoolingEer',
  ActiveCoolingEer = 'activeCoolingEer',
  ElectricityRequiredByTotal = 'electricityRequiredByTotal',
  ElectricityRequiredByHeating = 'electricityRequiredByHeating',
  ElectricityRequiredByCooling = 'electricityRequiredByCooling',
  ElectricityRequiredByRecyclingHeatingBasic = 'electricityRequiredByRecyclingHeatingBasic',
  ElectricityRequiredByRecyclingHeatingDhw = 'electricityRequiredByRecyclingHeatingDhw',
  ElectricityRequiredByHeatingBasic = 'electricityRequiredByHeatingBasic',
  ElectricityRequiredByHeatingDhw = 'electricityRequiredByHeatingDhw',
  ElectricityRequiredByFreeCooling = 'electricityRequiredByFreeCooling',
  ElectricityRequiredByActiveCooling = 'electricityRequiredByActiveCooling',
  TemperatureHeatingBasic = 'temperatureHeatingBasic',
  TemperatureHeatingDhw = 'temperatureHeatingDhw',
  TemperatureCooling = 'temperatureCooling',
  HeatCollectionTemperature = 'heatCollectionTemperature',
  TotalSourceHeating = 'totalSourceHeating',
  GroundSourceHeating = 'groundSourceHeating',
  AirSourceHeating = 'airSourceHeating',
  WasteSourceHeating = 'wasteSourceHeating',
  EnergyBusSourceHeating = 'energyBusSourceHeating',
  TotalSourceCooling = 'totalSourceCooling',
  GroundSourceCooling = 'groundSourceCooling',
  AirSourceCooling = 'airSourceCooling',
  EnergyBusSourceCooling = 'energyBusSourceCooling',
  RemainingCapacity = 'remainingCapacity',
}

type HybridHeatPumpExtraOutputProperties = {
  isPower: boolean
  isExported: boolean
}
export const hybridHeatPumpExtraOutputProperties: Record<
  HybridHeatPumpExtraOutputField,
  HybridHeatPumpExtraOutputProperties
> = {
  [HybridHeatPumpExtraOutputField.TotalHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RecycledHeatingBasic]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RecycledHeatingDhw]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.HeatingBasic]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.HeatingDhw]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TotalHeatingCop]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RecycledHeatingCopBasic]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RecycledHeatingCopDhw]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.HeatingCopBasic]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.HeatingCopDhw]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TotalCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RecycledCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.FreeCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ActiveCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TotalCoolingEer]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.FreeCoolingEer]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ActiveCoolingEer]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByTotal]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByHeating]: {
    isPower: true,
    isExported: false,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByCooling]: {
    isPower: true,
    isExported: false,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByRecyclingHeatingBasic]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByRecyclingHeatingDhw]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByHeatingBasic]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByHeatingDhw]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByFreeCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.ElectricityRequiredByActiveCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TemperatureHeatingBasic]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TemperatureHeatingDhw]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TemperatureCooling]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.HeatCollectionTemperature]: {
    isPower: false,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TotalSourceHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.GroundSourceHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.AirSourceHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.WasteSourceHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.EnergyBusSourceHeating]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.TotalSourceCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.GroundSourceCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.AirSourceCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.EnergyBusSourceCooling]: {
    isPower: true,
    isExported: true,
  },
  [HybridHeatPumpExtraOutputField.RemainingCapacity]: {
    isPower: false,
    isExported: true,
  },
}

type HybridHeatPumpExtraOutput<T> = Record<HybridHeatPumpExtraOutputField, T>
export type HybridHeatPumpExtraOutputValue = HybridHeatPumpExtraOutput<number>
export type HybridHeatPumpExtraOutputValues = HybridHeatPumpExtraOutput<
  number[]
>

export const hybridHeatPumpExtraOutputFields = Object.values(
  HybridHeatPumpExtraOutputField,
)

const initialHybridHeatPumpExtraOutputValues: HybridHeatPumpExtraOutputValues =
  hybridHeatPumpExtraOutputFields.reduce(
    (result, key) => ({ ...result, [key]: [] }),
    {} as Record<HybridHeatPumpExtraOutputField, number[]>,
  )

export class HybridHeatPumpProvider extends MultiTypeProvider<HybridHeatPumpParameters> {
  public readonly providedEnergyTypes = [EnergyType.Heating, EnergyType.Cooling]

  public isEnvironmentalHeatUsing = false

  public isConnectedToWasteHeat = false

  public extraOutputValues!: HybridHeatPumpExtraOutputValues

  private basicHeatCircuitsByDemandId!: Record<string, HeatCircuit>

  private dhwHeatCircuitsByDemandId!: Record<string, HeatCircuit>

  /**
   * We have to override this as we must handle each demand flow individually.
   */
  public provideForDemand(flows: EnergyFlow[]): EnergyFlow[] {
    this.initialize()

    const inputEnergyFlows =
      this.applyAllocations(flows).filter(isPrimaryEnergyFlow)

    const inputEnergyFlowsByEnergyType = groupBy(
      inputEnergyFlows,
      (flow) => flow.energyType,
    )
    this.inputFlows = createCombinedInputFlows(inputEnergyFlowsByEnergyType)

    const outputEnergyFlows = this.provide(inputEnergyFlows, [])

    this.flows = outputEnergyFlows.filter((flow) => flow.hourSeries.length > 0)

    return this.flows
  }

  protected initialize(): void {
    const { dhwHeatingMode } = this.parameters

    const { basicHeatCircuits, dhwHeatCircuits } = groupBy(
      this.parameters.heatCircuits ?? [],
      (circuit) =>
        circuit.isDhwCircuit && dhwHeatingMode === DhwHeatingMode.HeatAlways
          ? 'dhwHeatCircuits'
          : 'basicHeatCircuits',
    )

    this.basicHeatCircuitsByDemandId = keyBy(
      basicHeatCircuits,
      (circuit) => circuit.demandId,
    )
    this.dhwHeatCircuitsByDemandId = keyBy(
      dhwHeatCircuits,
      (circuit) => circuit.demandId,
    )

    this.isEnvironmentalHeatUsing = [
      HeatingOrCoolingSource.Air,
      HeatingOrCoolingSource.Ground,
    ].includes(this.parameters.baseHeatSource ?? HeatingOrCoolingSource.None)

    this.isConnectedToWasteHeat =
      Boolean(this.parameters.wasteHeatSourceAHourSeriesId) ||
      Boolean(this.parameters.wasteHeatSourceBHourSeriesId)

    // We have to deep copy this value because `extraOutputValues` will be mutated later on.
    this.extraOutputValues = cloneDeep(initialHybridHeatPumpExtraOutputValues)
  }

  protected getHourlyVariables(
    hour: number,
    demands: PrimaryHourlyEnergyFlow[],
  ): HybridHeatPumpHourlyVariables {
    const { remainingDemands, outdoorTemperature } = super.getHourlyVariables(
      hour,
      demands,
    )

    if (isNil(outdoorTemperature)) {
      throw new OutdoorTemperatureMissingError()
    }

    const { wasteHeatSourceAHourSeriesId, wasteHeatSourceBHourSeriesId } =
      this.parameters

    const wasteHeatSourceAHourSeries = wasteHeatSourceAHourSeriesId
      ? this.hourSeriesById[wasteHeatSourceAHourSeriesId]
      : undefined

    const wasteHeatSourceBHourSeries = wasteHeatSourceBHourSeriesId
      ? this.hourSeriesById[wasteHeatSourceBHourSeriesId]
      : undefined

    return {
      remainingDemands,
      outdoorTemperature,
      wasteHeatSourceAPower: wasteHeatSourceAHourSeries
        ? wasteHeatSourceAHourSeries.values[hour]
        : undefined,
      wasteHeatSourceBPower: wasteHeatSourceBHourSeries
        ? wasteHeatSourceBHourSeries.values[hour]
        : undefined,
    }
  }

  protected provideForHour({
    remainingDemands,
    outdoorTemperature,
    wasteHeatSourceAPower,
    wasteHeatSourceBPower,
  }: HybridHeatPumpHourlyVariables): HourlyEnergyFlow[] {
    const {
      performanceTableId,
      heatSourceAvailable,
      heatExhaustAvailable,
      heatRecycleAvailable,
      copModifier,
      heatingPower,
      heatingTemperatureCurveId,
      baseHeatSource,
      groundHeatSourceTemperature,
      groundHeatSourceMaxPower,
      airHeatSourceMinTemperature,
      wasteHeatSourceATemperature,
      wasteHeatSourceBTemperature,
      coolingTemperatureCurveId,
      freeCoolingSource,
      freeGroundCoolingMaxPower,
      freeAirCoolingMaxTemperature,
      freeCoolingEer,
      coolingSource,
      groundExhaustTemperature,
    } = this.parameters

    const performanceTable = this.performanceTablesById[performanceTableId]

    const preCalculationResults = preCalculateForHour({
      temperatureCurvesById: this.temperatureCurvesById,
      dhwHeatCircuitsByDemandId: this.dhwHeatCircuitsByDemandId,
      basicHeatCircuitsByDemandId: this.basicHeatCircuitsByDemandId,
      heatingTemperatureCurveId: heatingTemperatureCurveId ?? undefined,
      coolingTemperatureCurveId: coolingTemperatureCurveId ?? undefined,
      remainingDemands,
      outdoorTemperature,
    })

    const circulationCalculationResults = calculateCirculationForHour({
      performanceTable,
      heatRecycleAvailable,
      copModifier,
      heatingPower,
      preCalculationResults,
    })

    const heatingCalculationResults = calculateHeatingForHour({
      performanceTable,
      copModifier,
      heatSourceAvailable,
      heatingPower,
      baseHeatSource: baseHeatSource ?? undefined,
      groundHeatSourceTemperature: groundHeatSourceTemperature ?? undefined,
      groundHeatSourceMaxPower: groundHeatSourceMaxPower ?? undefined,
      airHeatSourceMinTemperature: airHeatSourceMinTemperature ?? undefined,
      wasteHeatSourceAPower,
      wasteHeatSourceATemperature: wasteHeatSourceATemperature ?? undefined,
      wasteHeatSourceBPower,
      wasteHeatSourceBTemperature: wasteHeatSourceBTemperature ?? undefined,
      basicHeatingTemperature: preCalculationResults.heating?.basic.temperature,
      dhwHeatingTemperature: preCalculationResults.heating?.dhw.temperature,
      basicHeatingDemand:
        circulationCalculationResults.heating?.basic.totalDemand,
      dhwHeatingDemand: circulationCalculationResults.heating?.dhw.totalDemand,
      powerCapacity: circulationCalculationResults.powerCapacity,
      outdoorTemperature,
    })

    const coolingCalculationResults = calculateCoolingForHour({
      performanceTable,
      copModifier,
      heatExhaustAvailable,
      heatingPower,
      freeCoolingSource: freeCoolingSource ?? undefined,
      freeGroundCoolingMaxPower: freeGroundCoolingMaxPower ?? undefined,
      freeAirCoolingMaxTemperature: freeAirCoolingMaxTemperature ?? undefined,
      freeCoolingEer: freeCoolingEer ?? undefined,
      coolingSource: coolingSource ?? undefined,
      groundExhaustTemperature: groundExhaustTemperature ?? undefined,
      coolingDemand: circulationCalculationResults.cooling.totalDemand,
      coolingTemperature: preCalculationResults.cooling?.temperature,
      powerCapacity: heatingCalculationResults.powerCapacity,
      outdoorTemperature,
    })

    const results = calculateResultsForHour({
      preCalculationResults,
      circulationCalculationResults,
      heatingCalculationResults,
      coolingCalculationResults,
      providerId: this.id,
      demands: remainingDemands,
      isDhwDemand: (demandId) =>
        this.dhwHeatCircuitsByDemandId[demandId] !== undefined,
    })

    this.saveExtraValues(results)

    return results.flows
  }

  private saveExtraValues(results: ResultsCalculationResults): void {
    hybridHeatPumpExtraOutputFields.forEach((key) => {
      // We have to mutate the array here, reallocating the array [8760 x fields] times is extremely slow.
      this.extraOutputValues[key].push(results[key])
    })
  }
}

export function isHybridHeatPumpProvider(
  provider: Provider,
): provider is HybridHeatPumpProvider {
  return provider instanceof HybridHeatPumpProvider
}
